import { symbol as symbol$1, equals } from '../../Equal/dist/effect-Equal.esm.js';
import { dual, pipe } from '../../Function/dist/effect-Function.esm.js';
import { symbol, combine, hash } from '../../Hash/dist/effect-Hash.esm.js';
import { toString, toJSON, NodeInspectSymbol } from '../../Inspectable/dist/effect-Inspectable.esm.js';
import { pipeArguments } from '../../Pipeable/dist/effect-Pipeable.esm.js';
import { isObject } from '../../Predicate/dist/effect-Predicate.esm.js';
import { empty as empty$1, fromIterable as fromIterable$1, has as has$1, insert, getOrder, forEach as forEach$1, removeFirst, size as size$1, keys } from '../../RedBlackTree/dist/effect-RedBlackTree.esm.js';

/**
 * @since 2.0.0
 */
const TypeId = /*#__PURE__*/Symbol.for("effect/SortedSet");

/**
 * @since 2.0.0
 * @category symbol
 */

/**
 * @since 2.0.0
 * @category models
 */

const SortedSetProto = {
  [TypeId]: {
    _A: _ => _
  },
  [symbol]() {
    return pipe(hash(this.keyTree), combine(hash(TypeId)));
  },
  [symbol$1](that) {
    return isSortedSet(that) && equals(this.keyTree, that.keyTree);
  },
  [Symbol.iterator]() {
    return keys(this.keyTree);
  },
  toString() {
    return toString(this.toJSON());
  },
  toJSON() {
    return {
      _id: "SortedSet",
      values: Array.from(this).map(toJSON)
    };
  },
  [NodeInspectSymbol]() {
    return this.toJSON();
  },
  pipe() {
    return pipeArguments(this, arguments);
  }
};
const fromTree = keyTree => {
  const a = Object.create(SortedSetProto);
  a.keyTree = keyTree;
  return a;
};

/**
 * @since 2.0.0
 * @category refinements
 */
const isSortedSet = u => isObject(u) && TypeId in u;

/**
 * @since 2.0.0
 * @category constructors
 */
const empty = O => fromTree(empty$1(O));

/**
 * @since 2.0.0
 * @category constructors
 */
const fromIterable = ord => iterable => fromTree(fromIterable$1(ord)(Array.from(iterable).map(k => [k, true])));

/**
 * @since 2.0.0
 * @category constructors
 */
const make = ord => (...entries) => fromIterable(ord)(entries);

/**
 * @since 2.0.0
 * @category elements
 */
const add = /*#__PURE__*/dual(2, (self, value) => has$1(self.keyTree, value) ? self : fromTree(insert(self.keyTree, value, true)));

/**
 * @since 2.0.0
 */
const difference = /*#__PURE__*/dual(2, (self, that) => {
  let out = self;
  for (const value of that) {
    out = remove(out, value);
  }
  return out;
});

/**
 * Check if a predicate holds true for every `SortedSet` element.
 *
 * @since 2.0.0
 * @category elements
 */
const every = /*#__PURE__*/dual(2, (self, refinement) => {
  for (const value of self) {
    if (!refinement(value)) {
      return false;
    }
  }
  return true;
});

/**
 * @since 2.0.0
 * @category filtering
 */
const filter = /*#__PURE__*/dual(2, (self, predicate) => {
  const ord = getOrder(self.keyTree);
  let out = empty(ord);
  for (const value of self) {
    if (predicate(value)) {
      out = add(out, value);
    }
  }
  return out;
});

/**
 * @since 2.0.0
 * @category sequencing
 */
const flatMap = /*#__PURE__*/dual(3, (self, O, f) => {
  let out = empty(O);
  forEach(self, a => {
    for (const b of f(a)) {
      out = add(out, b);
    }
  });
  return out;
});

/**
 * @since 2.0.0
 * @category traversing
 */
const forEach = /*#__PURE__*/dual(2, (self, f) => forEach$1(self.keyTree, f));

/**
 * @since 2.0.0
 * @category elements
 */
const has = /*#__PURE__*/dual(2, (self, value) => has$1(self.keyTree, value));

/**
 * @since 2.0.0
 */
const intersection = /*#__PURE__*/dual(2, (self, that) => {
  const ord = getOrder(self.keyTree);
  let out = empty(ord);
  for (const value of that) {
    if (has(self, value)) {
      out = add(out, value);
    }
  }
  return out;
});

/**
 * @since 2.0.0
 * @category elements
 */
const isSubset = /*#__PURE__*/dual(2, (self, that) => every(self, a => has(that, a)));

/**
 * @since 2.0.0
 * @category mapping
 */
const map = /*#__PURE__*/dual(3, (self, O, f) => {
  let out = empty(O);
  forEach(self, a => {
    const b = f(a);
    if (!has(out, b)) {
      out = add(out, b);
    }
  });
  return out;
});

/**
 * @since 2.0.0
 * @category filtering
 */
const partition = /*#__PURE__*/dual(2, (self, predicate) => {
  const ord = getOrder(self.keyTree);
  let right = empty(ord);
  let left = empty(ord);
  for (const value of self) {
    if (predicate(value)) {
      right = add(right, value);
    } else {
      left = add(left, value);
    }
  }
  return [left, right];
});

/**
 * @since 2.0.0
 * @category elements
 */
const remove = /*#__PURE__*/dual(2, (self, value) => fromTree(removeFirst(self.keyTree, value)));

/**
 * @since 2.0.0
 * @category getters
 */
const size = self => size$1(self.keyTree);

/**
 * Check if a predicate holds true for some `SortedSet` element.
 *
 * @since 2.0.0
 * @category elements
 */
const some = /*#__PURE__*/dual(2, (self, predicate) => {
  for (const value of self) {
    if (predicate(value)) {
      return true;
    }
  }
  return false;
});

/**
 * @since 2.0.0
 * @category elements
 */
const toggle = /*#__PURE__*/dual(2, (self, value) => has(self, value) ? remove(self, value) : add(self, value));

/**
 * @since 2.0.0
 */
const union = /*#__PURE__*/dual(2, (self, that) => {
  const ord = getOrder(self.keyTree);
  let out = empty(ord);
  for (const value of self) {
    out = add(value)(out);
  }
  for (const value of that) {
    out = add(value)(out);
  }
  return out;
});

/**
 * @since 2.0.0
 * @category getters
 */
const values = self => keys(self.keyTree);

export { add, difference, empty, every, filter, flatMap, forEach, fromIterable, has, intersection, isSortedSet, isSubset, make, map, partition, remove, size, some, toggle, union, values };
